// oxlint-disable typescript-eslint/prefer-readonly-parameter-types -- Storage and Location are external mutable DOM interfaces
// The deep link a notification email appends to a host page:
//
//   https://app.acme.com/orders?id=42#weloop:thread=<id>&project=<slug>&token=<t>
//   https://app.acme.com/#weloop:announcement=<id>&project=<slug>
//   https://app.acme.com/#weloop:survey=<id>&project=<slug>
//   https://app.acme.com/#weloop:view=notifications-settings&project=<slug>
//
// The format is written by `buildDeepLink` in `@weloop/core/widget`; the
// snippet bundle stays dependency-free, so the reader lives here. Change
// both together.
//
// The token can sign the recipient in, so two things happen the moment the
// snippet runs: the fragment is removed from the address bar (`replaceState`,
// no navigation) and the link is parked in `sessionStorage`. The parking
// covers a host site that redirects on arrival – a login wall, an SPA router
// that rewrites the URL – and would otherwise lose the fragment before the
// widget boots. The parked link is consumed once and expires quickly.
//
// `sessionStorage` (not `localStorage`) is deliberate: the link belongs to
// one tab and must not leak into a second tab or survive the tab closing.

/** The panel screens a link may open without naming an entity. */
export type DeepLinkView = "home" | "notifications-settings";

export type DeepLinkTarget =
	| { readonly announcementId: string; readonly kind: "announcement" }
	| { readonly feedbackId: string; readonly kind: "thread" }
	| { readonly kind: "survey"; readonly surveyId: string }
	| { readonly kind: "view"; readonly view: DeepLinkView };

export interface DeepLink {
	readonly projectSlug: string;
	readonly target: DeepLinkTarget;
	readonly token?: string;
}

const FRAGMENT_PREFIX = "#weloop:";
const STORAGE_KEY = "weloop-deep-link";
/** How long a parked link stays valid across the host site's redirects. */
export const DEEP_LINK_TTL_MS = 10 * 60 * 1000;

const isRecord = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

const str = (value: unknown): string | undefined =>
	typeof value === "string" && value !== "" ? value : undefined;

/**
 * A target from its stored shape: `{kind, ...}` matching `DeepLinkTarget`
 * exactly. Used both to validate a parked target and, keyed off the URL
 * params by the same field names the wire format already uses, to read a
 * fragment – `announcement`/`survey`/`thread` are the fragment's param
 * names and this target's own field names for the two IDs are `feedbackId`
 * and the two others, so one shape check serves both readers.
 */
const targetOf = (
	raw: Readonly<Record<string, unknown>>,
): DeepLinkTarget | undefined => {
	const feedbackId = str(raw["feedbackId"] ?? raw["thread"]);
	if (feedbackId !== undefined) {
		return { feedbackId, kind: "thread" };
	}
	const announcementId = str(raw["announcementId"] ?? raw["announcement"]);
	if (announcementId !== undefined) {
		return { announcementId, kind: "announcement" };
	}
	const surveyId = str(raw["surveyId"] ?? raw["survey"]);
	if (surveyId !== undefined) {
		return { kind: "survey", surveyId };
	}
	const { view } = raw;
	if (view === "home" || view === "notifications-settings") {
		return { kind: "view", view };
	}
	return undefined;
};

/** The deep link carried by a fragment, or undefined when it is not one. */
export const parseDeepLinkFragment = (hash: string): DeepLink | undefined => {
	if (!hash.startsWith(FRAGMENT_PREFIX)) {
		return undefined;
	}
	const params = new URLSearchParams(hash.slice(FRAGMENT_PREFIX.length));
	const raw: Record<string, string> = {};
	for (const [key, value] of params) {
		raw[key] = value;
	}
	const projectSlug = str(raw["project"]);
	const target = targetOf(raw);
	if (projectSlug === undefined || target === undefined) {
		return undefined;
	}
	const token = str(raw["token"]);
	return { projectSlug, target, ...(token === undefined ? {} : { token }) };
};

/** The tab's session storage, or undefined on a host page that blocks it. */
const resolveStorage = (): Storage | undefined => {
	try {
		return window.sessionStorage;
	} catch {
		return undefined;
	}
};

/**
 * Read a parked link back. Consumes it: a link is acted on once, and a
 * stale one is dropped rather than opening something out of nowhere later.
 */
export const takeParkedDeepLink = (
	storage: Storage,
	now: number,
): DeepLink | undefined => {
	try {
		const raw = storage.getItem(STORAGE_KEY);
		if (raw === null) {
			return undefined;
		}
		storage.removeItem(STORAGE_KEY);
		const parsed: unknown = JSON.parse(raw);
		if (!isRecord(parsed)) {
			return undefined;
		}
		const { parkedAt, target: rawTarget } = parsed;
		const projectSlug = str(parsed["projectSlug"]);
		const target = isRecord(rawTarget) ? targetOf(rawTarget) : undefined;
		if (
			projectSlug === undefined ||
			target === undefined ||
			typeof parkedAt !== "number" ||
			now - parkedAt > DEEP_LINK_TTL_MS
		) {
			return undefined;
		}
		const token = str(parsed["token"]);
		return { projectSlug, target, ...(token === undefined ? {} : { token }) };
	} catch {
		return undefined;
	}
};

/** Park a link for a later load of this tab. False when storage refused it. */
export const parkDeepLink = (
	storage: Storage,
	link: DeepLink,
	now: number,
): boolean => {
	try {
		storage.setItem(STORAGE_KEY, JSON.stringify({ ...link, parkedAt: now }));
		return true;
	} catch {
		// A locked-down storage only costs the redirect case; the link in hand
		// still opens the panel on this page load.
		return false;
	}
};

/**
 * Strip the deep-link fragment from the address bar without a navigation, so
 * a reload, a bookmark, or a copied address never carries the token.
 */
const stripFragment = (location: Location): void => {
	try {
		window.history.replaceState(
			window.history.state,
			"",
			`${location.pathname}${location.search}`,
		);
	} catch {
		// Some sandboxed pages refuse history writes; the parked copy is
		// consumed regardless, and the token is single-use on the server.
	}
};

/**
 * The deep link this page load should act on, if any: the fragment on the
 * address bar first, otherwise a link parked by an earlier load of this
 * tab that the host site redirected away from. Either way it is consumed –
 * a second call returns nothing.
 */
export const takeDeepLink = (now = Date.now()): DeepLink | undefined => {
	const storage = resolveStorage();
	const fromFragment = parseDeepLinkFragment(window.location.hash);
	if (fromFragment !== undefined) {
		stripFragment(window.location);
		if (storage !== undefined) {
			storage.removeItem(STORAGE_KEY);
		}
		return fromFragment;
	}
	return storage === undefined ? undefined : takeParkedDeepLink(storage, now);
};

/**
 * Park a fragment link before the host page has a chance to redirect, and
 * take the token off the address bar at once. Runs at the very top of the
 * bundle, before the config fetch: that fetch is the window in which a login
 * wall sends the tab away, and host scripts (analytics, session replay) read
 * the URL. When storage refuses the link, the fragment stays for
 * `takeDeepLink` – the only copy left.
 */
export const parkDeepLinkFromFragment = (now = Date.now()): void => {
	const link = parseDeepLinkFragment(window.location.hash);
	if (link === undefined) {
		return;
	}
	const storage = resolveStorage();
	if (storage !== undefined && parkDeepLink(storage, link, now)) {
		stripFragment(window.location);
	}
};
